import _ from 'lodash';
import React, { useEffect, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { Info, Download, Loader2, Box } from 'lucide-react';
import Select from 'src/components/ui/BrandSelect';
import { Actions as ADMINACTIONS } from '../../../store/actions/adminActions';
import { Actions as AUTHACTIONS } from '../../../store/actions/authenticationActions';
import { Button } from '../../ui/button';
import Breadcrumb from '../../Common/Breadcrumb';
import { adminPortalSelect } from '../../utils/ReactSelectStyles.js';
import ValidationMessage from '../../utils/ValidationMessage';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import WhiteLabelBrandingAssetUpload from '../WhiteLabelBrandingAssetUpload';

const BRANDING_UPLOAD_FIELD_KEYS = ['logo', 'favicon', 'logoEmail'];

const Settings = ({
  settingsOpt,
  saveSettings,
  user,
  cyfAdmin,
  whiteLabelAdmin,
  consultantAdmin,
  TextArea,
  getAllItemsFromDB,
  options,
  wl,
  whiteLabels,
  hideFields,
  toTitleCase,
  editableFields,
  saveWLSettings,
  isValidUrl,
  branding,
  showLoader,
  repackageFactors,
  presignWhiteLabelBranding,
  replaceBrandingAssetPreview,
  clearBrandingAssetPreview,
}) => {
  const [editedState, setEditedState] = useState({});
  const [error, setError] = useState([]);
  const [currentWl, setCurrentWl] = useState(wl?.whiteLabelId);
  const [wlSettings, setWlSettings] = useState(settingsOpt);
  const [activeWlRow, setActiveWlRow] = useState({});
  const [pendingBranding, setPendingBranding] = useState({});
  const { primaryColor, tooltipBackground, tooltipText } = branding?.colors || {};

  useEffect(() => {
    const row = whiteLabels?.find((x) => x.whiteLabelId === currentWl);
    if (row) setActiveWlRow(row);
  }, [currentWl, whiteLabels]);

  const setBrandingPending = useCallback((key, val) => {
    setPendingBranding((prev) => {
      const next = { ...prev };
      if (val === undefined) delete next[key];
      else next[key] = val;
      return next;
    });
  }, []);

  useEffect(() => {
    if (!cyfAdmin) return;
    const o = {};
    BRANDING_UPLOAD_FIELD_KEYS.forEach((k) => {
      if (pendingBranding[k] !== undefined) o[k] = pendingBranding[k];
    });
    replaceBrandingAssetPreview(Object.keys(o).length ? o : null);
  }, [cyfAdmin, pendingBranding, replaceBrandingAssetPreview]);

  useEffect(
    () => () => {
      if (cyfAdmin) clearBrandingAssetPreview();
    },
    [cyfAdmin, clearBrandingAssetPreview],
  );

  function updateSettings(wl) {
    let settingOpt = {};
    Object.keys(wl)?.forEach((item) => {
      if (hideFields.includes(item)) return;
      settingOpt[item] = {
        type: 'text',
        title: toTitleCase(item),
        value: String(wl[item]) || String(editedState[item]),
        disable: whiteLabelAdmin ? true : editableFields.includes(item) ? false : true,
      };
    });
    setWlSettings(settingOpt);
  }

  useEffect(() => {
    getAllItemsFromDB('whiteLabelSettings', true);
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    updateSettings(wl);
    //eslint-disable-next-line
  }, [whiteLabels]);

  const updateField = (e) => {
    const { name, value } = e.target;
    const newEditedState = { ...editedState };

    newEditedState[name] = value;
    setEditedState(newEditedState);
  };

  /* const handleSave = async (settings) => {
    try {
      await saveSettings(settings);
    } catch (error) {
      if (error.inner) {
        // If here, it means there are validation errors
        setError(error.inner);
      } else console.log('Error', error);
    }
  }; */

  const saveWlSettingsLocal = async () => {
    try {
      const settings = { ...activeWlRow, ...editedState, whiteLabelId: currentWl };
      if (pendingBranding.logo !== undefined) settings.logo = pendingBranding.logo;
      if (pendingBranding.favicon !== undefined) settings.favicon = pendingBranding.favicon;
      if (pendingBranding.logoEmail !== undefined) settings.logoEmail = pendingBranding.logoEmail;
      await saveWLSettings(settings);
      setPendingBranding({});
      clearBrandingAssetPreview();
    } catch (error) {
      if (error.inner) {
        setError(error.inner);
      } else {
        console.log('Error', error);
      }
      throw error;
    }
  };

  return (
    <TooltipProvider>
      <Breadcrumb items={[{ label: 'Settings', link: '/admin/settingsPage' }]} />
      {!consultantAdmin && (
        <div className='row'>
          <div className='col-lg-12'>
            <section className='panel'>
              <aside className='profile-nav alt'>
                <section className='panel'>
                  <header
                    className='panel-heading'
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {whiteLabels ? whiteLabels?.find((wl) => wl.whiteLabelId === currentWl)?.name + ' Settings' : 'Settings'}
                    {cyfAdmin && (
                      <Select
                        name='whiteLabels'
                        options={options}
                        onChange={(selected) => {
                          setCurrentWl(selected?.value);
                          setEditedState({});
                          setPendingBranding({});
                          updateSettings(whiteLabels?.find((wl) => wl.whiteLabelId === selected.value));
                        }}
                        styles={adminPortalSelect}
                        menuPlacement='auto'
                        value={options.find((option) => option?.value === currentWl)}
                      />
                    )}
                  </header>
                  {cyfAdmin && currentWl && (
                    <div className='px-4 py-4 border-b border-border space-y-4 max-w-3xl'>
                      <p className='text-sm font-semibold text-foreground'>Branding images (upload)</p>
                      <p className='text-xs text-muted-foreground'>
                        Preview updates in the app until you save. SVG supported for logo and favicon; not for email logo.
                      </p>
                      <WhiteLabelBrandingAssetUpload
                        assetType='logo'
                        label='App logo'
                        whiteLabelId={currentWl}
                        savedUrl={activeWlRow.logo}
                        pendingValue={pendingBranding.logo}
                        onPendingChange={(v) => setBrandingPending('logo', v)}
                        presignWhiteLabelBranding={presignWhiteLabelBranding}
                        disabled={showLoader}
                      />
                      <WhiteLabelBrandingAssetUpload
                        assetType='favicon'
                        label='Favicon'
                        whiteLabelId={currentWl}
                        savedUrl={activeWlRow.favicon}
                        pendingValue={pendingBranding.favicon}
                        onPendingChange={(v) => setBrandingPending('favicon', v)}
                        presignWhiteLabelBranding={presignWhiteLabelBranding}
                        disabled={showLoader}
                      />
                      <WhiteLabelBrandingAssetUpload
                        assetType='logoEmail'
                        label='Email logo'
                        whiteLabelId={currentWl}
                        savedUrl={activeWlRow.logoEmail}
                        pendingValue={pendingBranding.logoEmail}
                        onPendingChange={(v) => setBrandingPending('logoEmail', v)}
                        presignWhiteLabelBranding={presignWhiteLabelBranding}
                        disabled={showLoader}
                      />
                    </div>
                  )}
                  {wlSettings && (
                    <ul className='nav nav-pills nav-stacked'>
                      {_.sortBy(Object.keys(wlSettings), (o) => wlSettings[o].title)
                        .filter(
                          (o) =>
                            (!cyfAdmin || !BRANDING_UPLOAD_FIELD_KEYS.includes(o)) &&
                            (!wlSettings[o]?.exclusiveFor || wlSettings[o]?.exclusiveFor.includes(user)),
                        )
                        .map((key, index) => {
                          const type = wlSettings[key].type;
                          //const styles = wlSettings[key].style;
                          const value = wlSettings[key].value;
                          return (
                            <li key={index}>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  padding: '10px 15px',
                                }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                                  {wlSettings[key].title}
                                  {wlSettings[key].tooltipText && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Info size={16} strokeWidth={1.5} className='ml-2.5 text-primary cursor-pointer' />
                                      </TooltipTrigger>
                                      <TooltipContent side='right'>
                                        <span className='text-xs text-popover-foreground'>{wlSettings[key].tooltipText}</span>
                                      </TooltipContent>
                                    </Tooltip>
                                  )}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                  {['text', 'email', 'password'].includes(type) &&
                                    TextArea({ wlSettings, key, editedState, value, updateField, type })}
                                  {isValidUrl(value) && (
                                    <div className='ml-[5px]' style={{ cursor: 'pointer' }} onClick={() => window.open(value)}>
                                      <Download size={16} strokeWidth={1.5} />
                                    </div>
                                  )}
                                </div>
                              </div>
                              {error.length > 0 && (
                                <ValidationMessage
                                  message={_.find(error, { path: key })?.message}
                                  style={{ fontSize: 12 }}
                                  wrapperStyle={{ display: 'flex', justifyContent: 'flex-end', marginRight: 20 }}
                                />
                              )}
                            </li>
                          );
                        })}
                    </ul>
                  )}
                </section>
              </aside>
            </section>
            <div
              id='save-wl-settings'
              style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', margin: '20px 0px' }}>
              {cyfAdmin && (
                <>
                  <Button
                    disabled={showLoader}
                    onClick={() => repackageFactors('whiteLabelSettings')}
                    style={{ marginRight: '1rem' }}>
                    {showLoader ? <Loader2 size={16} strokeWidth={1.5} className="animate-spin" style={{ marginRight: '5px' }} /> : <Box size={16} strokeWidth={1.5} style={{ marginRight: '5px' }} />}
                    Re-Package
                  </Button>
                  <Button
                    onClick={async () => {
                      try {
                        await saveWlSettingsLocal();
                        window.location.reload();
                      } catch {
                        /* toast from saveWLSettings */
                      }
                    }}>
                    Save White Label Settings
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* <ListPaymentMethods /> */}
    </TooltipProvider>
  );
};

const mapStateToProps = (state) => {
  const isValidUrl = (urlString) => {
    var urlPattern = new RegExp(
      '^(https:\\/\\/)' + // validate protocol
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // validate domain name
        '((\\d{1,3}\\.){3}\\d{1,3}))' + // validate OR ip (v4) address
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // validate port and path
        '(\\?[;&a-z\\d%_.~+=-]*)?' + // validate query string
        '(\\#[-a-z\\d_]*)?$',
      'i',
    ); // validate fragment locator
    return !!urlPattern.test(urlString);
  };

  function TextArea({ wlSettings, key, editedState, value, updateField, type }) {
    return (
      <textarea
        style={{
          minWidth: '30vw',
          width: '40vw',
          resize: 'none',
        }}
        disabled={wlSettings?.[key]?.disable}
        className={`flex min-h-[80px] w-full rounded-lg border-[1.5px] border-input bg-transparent px-3.5 py-2 text-sm resize-none ${!isValidUrl(value) ? 'mr-[20px]' : ''}`}
        name={key}
        value={editedState?.[key] === undefined ? value : editedState[key]}
        onChange={updateField}
        type={type}
        rows={4}
        maxRows={4}
        sx={!isValidUrl(value) ? { marginRight: '25px' } : { marginRight: '10px' }}
      />
    );
  }

  function toTitleCase(name) {
    // Split the string at each capital letter
    let formattedString = name.replace(/([A-Z])/g, ' $1');
    // Capitalize the first letter and remove leading space
    formattedString = formattedString.charAt(0).toUpperCase() + formattedString.slice(1);
    if (formattedString.includes('U R L')) {
      formattedString = formattedString.replace('U R L', 'URL');
    }
    return formattedString;
  }

  const editableFields = [
    'activityDataInventoryFile',
    'carbonReportLogo',
    'dataHelperTool',
    'favicon',
    'industriesAndFacilitiesTypesGuide',
    'logo',
    'logoEmail',
    'methodology',
    'tutorialURL',
    'usageDataGuide',
    'scienceBasedTarget',
    'faq',
    'facilitiesTutorial',
    'importTemplatesTutorial',
    'organisationSetUpTutorial',
    'reportsTutorial',
    'usageDataTutorial',
  ];

  const hideFields = [
    'annualPriceOptions',
    'annualStripePriceId',
    'brandingColors',
    'font',
    'enableConsultants',
    'hasPaymentSystem',
    'inviteUsers',
    'showConsultantClients',
    'stripeProductId',
    'stripeAccountId',
    'stripePercentageFee',
    'unlimitedStripePriceId',
    'UnlimitedPriceOptions',
    'whiteLabelId',
  ];

  const settingsOpt = {};
  const wl = state.whiteLabelAdmin;
  const { cyfAdmin, whiteLabelAdmin, consultantAdmin } = state.profile.details || {};
  const user = cyfAdmin ? 'CYF' : whiteLabelAdmin ? 'White Label' : consultantAdmin ? 'Consultant' : null;

  Object.keys(wl)?.forEach((item) => {
    if (hideFields.includes(item)) return null;
    settingsOpt[item] = {
      type: 'text',
      title: toTitleCase(item),
      value: String(wl[item]) || '',
      disable: whiteLabelAdmin ? true : editableFields.includes(item) ? false : true,
    };
  });

  const whiteLabels = state.admin?.adminPortal?.whiteLabelSettings;
  const options = _.sortBy(whiteLabels?.map((wl) => ({ value: wl.whiteLabelId, label: wl.name })) || [], ['label']);
  const branding = state.branding;

  return {
    settingsOpt,
    cyfAdmin,
    whiteLabelAdmin,
    consultantAdmin,
    user,
    TextArea,
    options,
    wl,
    hideFields,
    editableFields,
    toTitleCase,
    whiteLabels,
    branding,
    isValidUrl,
    showLoader: state?.showLoader,
  };
};

const mapDispatchToProps = (dispatch) => ({
  saveWLSettings: (settings) => dispatch(ADMINACTIONS.saveWLSettings(settings)),
  getAllItemsFromDB: (table, force) => dispatch(ADMINACTIONS.getAllItemsFromDB(table, null, force)),
  getWLSettings: (options) => dispatch(AUTHACTIONS.getWLSettings(options)),
  repackageFactors: (type) => dispatch(ADMINACTIONS.repackageFactors(type)),
  presignWhiteLabelBranding: (body) => dispatch(ADMINACTIONS.presignWhiteLabelBranding(body)),
  replaceBrandingAssetPreview: (p) => dispatch(ADMINACTIONS.replaceBrandingAssetPreview(p)),
  clearBrandingAssetPreview: () => dispatch(ADMINACTIONS.clearBrandingAssetPreview()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Settings);
